import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import LoginBox from 'features/feature_user/components/LoginBox';
import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from "@auth0/auth0-react";
import useAuth0ServerLogin from 'features/feature_user/hooks/useAuth0ServerLogin';
import useProfile from 'features/feature_user/hooks/UseProfile';
import { HttpService } from 'core/services/http';
import { performAppLogout } from 'core/auth/performAppLogout';
import { useQueryClient } from 'react-query';
import { Menu, MenuItem, Divider, Avatar } from '@mui/material';
import { getProfileAvatarUrl, nameToAvatarColor } from 'features/feature_job/utils/profileDisplay';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationDropdown from 'features/feature_user/components/NotificationDropdown';

function HeaderSignInButton() {

    const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [authState, setAuthState] = useState(() => {
        // Initialize state from localStorage
        return {
            user: HttpService.getUser(),
            token: HttpService.getToken()
        };
    });

    const { user: auth0User, isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0();
    const { data: serverTokenData, isLoading, isError, refetchAuth } = useAuth0ServerLogin();
    
    // Check backend authentication from state
    const backendUser = authState.user;
    const backendToken = authState.token;
    const isBackendAuthenticated = !!(backendUser && backendToken);
    
    // Load profile for backend users to get avatar and role (only if backend authenticated and not Auth0)
    const shouldLoadProfile = isBackendAuthenticated && !isAuth0Authenticated;
    const { data: profile, isLoading: profileLoading } = useProfile({ 
        enabled: shouldLoadProfile 
    });
    
    // Get role from profile or localStorage user object
    const userRole = profile?.role || backendUser?.role;

    // Determine if user is authenticated via either method
    const isAuthenticated = isAuth0Authenticated || isBackendAuthenticated;

    // Get user info from either source
    const getUserName = () => {
        if (isAuth0Authenticated && auth0User?.name) {
            return auth0User.name;
        }
        if (isBackendAuthenticated && backendUser) {
            if (backendUser.first_name && backendUser.last_name) {
                return `${backendUser.first_name} ${backendUser.last_name}`;
            }
            if (backendUser.first_name) {
                return backendUser.first_name;
            }
            if (backendUser.email) {
                return backendUser.email.split('@')[0];
            }
        }
        return 'User';
    };

    useEffect(() => {
        if (isAuth0Authenticated) {
          // Optionally, refetch the server login when the authentication status changes.
          refetchAuth();
        }
    }, [isAuth0Authenticated]);

    // Check for changes in localStorage (for backend auth)
    useEffect(() => {
        const checkAuth = () => {
            const user = HttpService.getUser();
            const token = HttpService.getToken();
            setAuthState({ user, token });
        };

        // Check immediately
        checkAuth();

        // Set up interval to check for changes (e.g., after login)
        const interval = setInterval(checkAuth, 1000);

        // Also listen for storage events (in case of multiple tabs)
        window.addEventListener('storage', checkAuth);

        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', checkAuth);
        };
    }, []);

    const handleSignInClick = (e) => {
        e.preventDefault();
        setLoginModalIsOpen(true);
    };

    const handleMenuClick = (event) => {
        event.preventDefault();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleProfileClick = () => {
        handleMenuClose();
        navigate('/profile');
    };

    const handleDashboardClick = () => {
        handleMenuClose();
        navigate('/employer/dashboard');
    };

    const handleJobseekerDashboardClick = () => {
        handleMenuClose();
        navigate('/jobseeker/dashboard');
    };

    const handleNotificationsClick = () => {
        handleMenuClose();
        // Navigate to the appropriate dashboard notifications route based on user role
        const role = userRole || HttpService.getUser()?.role;
        if (role === 'employee') {
            navigate('/jobseeker/notifications');
        } else if (role === 'employer') {
            navigate('/employer/notifications');
        } else {
            navigate('/notifications');
        }
    };

    const handleLogout = () => {
        handleMenuClose();
        performAppLogout(queryClient, {
            isAuth0Authenticated,
            auth0Logout,
        });
    };

    // Check if user is an employee or employer using profile role
    const isEmployee = isBackendAuthenticated && (userRole === 'employee' || backendUser?.role === 'employee');
    const isEmployer = isBackendAuthenticated && (userRole === 'employer' || backendUser?.role === 'employer');

    const headerMenuName = isAuthenticated ? getUserName() : '';
    const headerMenuAvatarSrc =
        isAuthenticated && isAuth0Authenticated && auth0User?.picture
            ? auth0User.picture
            : isAuthenticated && isBackendAuthenticated && profile
              ? getProfileAvatarUrl(profile) || undefined
              : undefined;
    const headerMenuAvatarLetter =
        headerMenuName.trim().charAt(0).toUpperCase() || '?';

    return (
        <>
            {isAuthenticated ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {isEmployee && <NotificationDropdown />}
                    <div style={{ position: 'relative' }}>
                        <div
                            onClick={handleMenuClick}
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '20px',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        {(!isBackendAuthenticated || !profileLoading) && (
                            <Avatar
                                src={headerMenuAvatarSrc}
                                alt={headerMenuName}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    border: '2px solid #fff',
                                    bgcolor: nameToAvatarColor(headerMenuName),
                                }}
                            >
                                {headerMenuAvatarLetter}
                            </Avatar>
                        )}
                        {profileLoading && isBackendAuthenticated && (
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <span style={{ fontSize: '12px' }}>...</span>
                            </div>
                        )}
                        <span style={{ 
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#333'
                        }}>
                            {headerMenuName}
                        </span>
                        <svg 
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                                transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                            }}
                        >
                            <path 
                                d="M6 9L1 4H11L6 9Z" 
                                fill="#666"
                            />
                        </svg>
                    </div>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            style: {
                                marginTop: '8px',
                                minWidth: '180px',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px',
                            }
                        }}
                    >
                        <MenuItem 
                            onClick={handleProfileClick}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 16px',
                                fontSize: '14px'
                            }}
                        >
                            <PersonIcon fontSize="small" />
                            Profile
                        </MenuItem>
                        {isEmployee && (
                            <MenuItem 
                                onClick={handleNotificationsClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '10px 16px',
                                    fontSize: '14px'
                                }}
                            >
                                <NotificationsIcon fontSize="small" />
                                Notifications
                            </MenuItem>
                        )}
                        {isEmployer && (
                            <>
                                <MenuItem 
                                    onClick={handleDashboardClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 16px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <DashboardIcon fontSize="small" />
                                    Dashboard
                                </MenuItem>
                                <Divider />
                            </>
                        )}
                        {isEmployee && (
                            <>
                                <MenuItem 
                                    onClick={handleJobseekerDashboardClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 16px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <DashboardIcon fontSize="small" />
                                    Dashboard
                                </MenuItem>
                                <Divider />
                            </>
                        )}
                        {(!isBackendAuthenticated || (isBackendAuthenticated && backendUser?.role !== 'employer' && backendUser?.role !== 'employee')) && !isAuth0Authenticated && <Divider />}
                        <MenuItem 
                            onClick={handleLogout}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 16px',
                                fontSize: '14px',
                                color: '#d32f2f'
                            }}
                        >
                            <ExitToAppIcon fontSize="small" />
                            Sign out
                        </MenuItem>
                    </Menu>
                    </div>
                </div>
            ) : (
                <Link onClick={handleSignInClick} className="btn btn-sm btn-default btn-shadow ml-40 hover-up">Sign in</Link>
            )}

            <CustomModal  key={loginModalIsOpen} setOpen={(value) => setLoginModalIsOpen(value)}
                open={loginModalIsOpen} showClose={true}>
                    <LoginBox onClose={() => setLoginModalIsOpen(false)} />
            </CustomModal>
        </>
    )
}

export default HeaderSignInButton